// Login.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from '../styles/Auth.module.css';
import { useAuth } from '../context/AuthContext';
import { RealTimeFeedbackIcon, SlidesIcon, AICoachIcon } from '../components/Icons/FeatureIcons';
import GoogleAuthButton from '../components/GoogleAuthButton/GoogleAuthButton';
import { useDocumentHead } from '../hooks/useDocumentHead';
import ForgotPassword from '../components/PasswordReset/ForgetPassword';

const Login = ({ onLoginSuccess }) => {
  useDocumentHead(
    'Sign In | TopSpeaks',
    'Sign in to TopSpeaks to access AI-powered speaking tools, real-time feedback, and expert coaching. Unlock your speaking potential with advanced analytics and personalized guidance.',
    'index, follow'
  );
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await login(formData);
      onLoginSuccess();
      navigate('/dashboard');
    } catch (err) {
      if (err.message.includes('Session expired')) {
        alert('Invalid email or password');
      } else {
        setError(err.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.contentWrapper}>
        <div className={styles.brandSection}>
          <div className={styles.brandContent}>
            <h1>TopSpeaks</h1>
            <h2>Empower Your Voice</h2>
            <p>Advanced AI-powered platform for students & professionals to perfect their speaking skills through practice with detailed feedback and analytics.</p>
            <div className={styles.features}>
              <div className={styles.featureItem}>
                <span className={styles.featureIcon}><RealTimeFeedbackIcon /></span>
                <span>Real-Time Feedback for Improved Delivery</span>
              </div>
              <div className={styles.featureItem}>
                <span className={styles.featureIcon}> <SlidesIcon /> </span>
                <span>Instant AI-Generated Presentation Slides</span>
              </div>
              <div className={styles.featureItem}>
                <span className={styles.featureIcon}> <AICoachIcon /></span>
                <span>Scenario Based Voice Chat AI Coach</span>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.formSection}>
          <div className={styles.formContainer}>
            <h2>Welcome Back</h2>
            <p className={styles.formSubtitle}>Continue your presentation mastery journey</p>

            {error && <div className={styles.error}>{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className={styles.inputGroup}>
                <label>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@company.com"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className={styles.inputGroup}>
                <div className={styles.passwordHeader}>
                  <div className={styles.passwordLabel}>
                    <label > Password</label>
                    <button
                      type="button"
                      className={styles.forgotPasswordLink}
                      onClick={() => setShowForgotPassword(true)}
                    >
                      Forgot Password?
                    </button>
                  </div>

                </div>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <button
                type="submit"
                className={styles.submitButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Signing in...' : 'Sign In'}
              </button>
              <p className={styles.termsText}>
                By logging in, you agree to our{' '}
                <Link to="/terms" className={styles.termsLink}>Terms and Conditions</Link> and{' '}
                <Link to="/privacy" className={styles.termsLink}>Privacy Policy</Link>
              </p>
            </form>
            {showForgotPassword && (
              <ForgotPassword onClose={() => setShowForgotPassword(false)} />
            )}
            <div className={styles.formFooter}>
              <p>
                New to TopSpeaks? <Link to="/signup">Create an account</Link>
              </p>
            </div>
            <div className={styles.divider}>or</div>
            <GoogleAuthButton />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
